import { IntervalUnit, LBNodeData } from '@/proto/logiblocks/logiblocks';
import { ACTION_ICON_COLOR } from '../../styles';
import {
  type GraphData,
  type LBNodeInfo,
  NodeCategory,
  OutputType,
} from '../../types';
import { MSG_REQUIRED, type ValidationError } from '../../validation';
import { SnoozeActionCustomization } from './SnoozeActionCustomization';
import { DEFAULT_SNOOZE_ACTION, MAX_STORABLE_SNOOZE_SECONDS } from './defaults';
import { defaultNodeData } from '../../graphUtils';
import { formatUtcSecondsAsUserTime } from '@/pages/Logiblocks/dateTimeUtils';

/** Validation field names for the Snooze action. Shared with the UI. */
export const SnoozeActionFields = {
  ALERT_TRIGGER: 'Alert trigger',
  SNOOZE_DURATION: 'Snooze duration',
  TIME: 'Time',
} as const;

/**
 * The Material "notifications_paused" glyph from the Snooze node's Figma design, in the colour
 * every other Action node's icon uses.
 */
const SnoozeIcon = ({ size }: { size: number }) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M12 21.75C13.1 21.75 14 20.85 14 19.75H10C10 20.85 10.89 21.75 12 21.75ZM18 15.75V10.75C18 7.68 16.36 5.11 13.5 4.43V3.75C13.5 2.92 12.83 2.25 12 2.25C11.17 2.25 10.5 2.92 10.5 3.75V4.43C7.63 5.11 6 7.68 6 10.75V15.75L4 17.75V18.75H20V17.75L18 15.75ZM14.5 9.55L11.7 12.95H14.5V14.75H9.5V12.95L12.3 9.55H9.5V7.75H14.5V9.55Z'
      fill={ACTION_ICON_COLOR}
    />
  </svg>
);

/**
 * Format the snooze configuration for display in the node subtitle.
 */
function formatDuration(data: LBNodeData): string {
  const snoozeData = data.snooze_action;

  // Handle "until_time" case
  if (snoozeData?.until_time !== undefined) {
    if (snoozeData.until_time.seconds_since_midnight === undefined) {
      return '(unconfigured)';
    }
    const timeStr = formatUtcSecondsAsUserTime(
      snoozeData.until_time.seconds_since_midnight
    );
    return `Until ${timeStr}`;
  }

  // Handle "duration" case
  if (!snoozeData?.duration?.seconds) {
    return '(unconfigured)';
  }

  const unit = snoozeData.duration.display_unit ?? IntervalUnit.MINUTES;
  const amount =
    unit === IntervalUnit.HOURS
      ? snoozeData.duration.seconds / 3600
      : snoozeData.duration.seconds / 60;

  const unitLabel = unit === IntervalUnit.HOURS ? 'hour' : 'minute';
  const pluralizedUnit = amount === 1 ? unitLabel : `${unitLabel}s`;

  return `For ${amount} ${pluralizedUnit}`;
}

export const SnoozeActionNodeInfo: LBNodeInfo = {
  protobufNodeDataKey: 'snooze_action',
  category: NodeCategory.ACTION,
  label: 'Snooze alert',
  hasInput: true,
  getIcon: (size: number) => <SnoozeIcon size={size} />,
  outputType: OutputType.SINGLE,
  getSubtitle: (data: LBNodeData, _graph: GraphData) => {
    return formatDuration(data);
  },

  getValidationErrors: (
    _nodeId: string,
    nodeData: LBNodeData,
    graph: GraphData
  ) => {
    const snoozeData = nodeData.snooze_action;
    const errors: ValidationError[] = [];

    // Snooze holds back the alert that started the run, so the workflow needs an alert trigger.
    if (!graph.nodes.some((node) => node.data?.alert_trigger !== undefined)) {
      errors.push({
        type: 'VALUE_ERROR',
        field: SnoozeActionFields.ALERT_TRIGGER,
        message:
          'Snooze can only be used in a workflow that starts from an alert trigger.',
      });
    }

    if (snoozeData?.until_time !== undefined) {
      // "until_time" mode - requires a time picked.
      if (snoozeData.until_time.seconds_since_midnight === undefined) {
        errors.push({
          type: 'REQUIRED_FIELD',
          field: SnoozeActionFields.TIME,
          message: MSG_REQUIRED,
        });
      }
    } else if (
      !snoozeData?.duration?.seconds ||
      snoozeData.duration.seconds <= 0
    ) {
      // "duration" mode - requires a positive duration
      errors.push({
        type: 'REQUIRED_FIELD',
        field: SnoozeActionFields.SNOOZE_DURATION,
        message: MSG_REQUIRED,
      });
    } else if (snoozeData.duration.seconds > MAX_STORABLE_SNOOZE_SECONDS) {
      errors.push({
        type: 'VALUE_ERROR',
        field: SnoozeActionFields.SNOOZE_DURATION,
        message: 'Snooze is too long to store.',
      });
    }

    return errors.length > 0 ? errors : undefined;
  },

  CustomizationComponent: SnoozeActionCustomization,
  createDefaultNodeData: () =>
    defaultNodeData('snooze_action', DEFAULT_SNOOZE_ACTION),
};
